import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import processRelatedProducts from '../util/processRelatedProducts';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import PreorderModeContext from '../../preorder/PreorderModeContext';
import type { Product } from '../types';
import usePriceQueryOptions from '../../pricing/hooks/usePriceQueryOptions';
import productDetailsQuery from './productDetailsQuery';

interface LoadingParams {
    forceLoggedOut?: boolean;
    includeRelatedItemsPrice?: boolean;
    includePrice?: boolean;
    id?: string;
}

export const useProductDetailsQuery = ({
    forceLoggedOut = false,
    includeRelatedItemsPrice = false,
    includePrice = false,
    id,
}: LoadingParams) => {
    const { store, language } = useStoreAndLanguageParams();
    const { productsFilters } = useContext(PreorderModeContext);

    const { variables, loadingPriceQueryOptions } =
        usePriceQueryOptions(forceLoggedOut);
    const includePrices = includeRelatedItemsPrice || includePrice;
    const skipByPriceLoading = loadingPriceQueryOptions && includePrices;

    const result = useQuery<{
        product: Product;
    }>(productDetailsQuery, {
        variables: {
            store,
            language,
            id,
            ...productsFilters,
            ...variables,
            includeRelatedItemsPrice,
            includePrice,
        },
        skip: skipByPriceLoading,
    });

    return {
        ...result,
        loading: skipByPriceLoading || result.loading,
    };
};

const useProductDetails = (params?: Omit<LoadingParams, 'id'>) => {
    const { variantId, pid } = useParams<{
        variantId?: string;
        pid: string;
    }>();

    const { loading, error, data } = useProductDetailsQuery({
        ...params,
        id: pid,
    });

    if (error) {
        throw error;
    }

    const product = useMemo(() => {
        return data?.product ? processRelatedProducts(data.product) : undefined;
    }, [data?.product]);

    if (!data?.product) {
        return {
            loading,
            product: product,
        };
    }

    return {
        loading,
        product,
        variantId,
    };
};

export default useProductDetails;
